import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Announcement,
  Button,
  EditableTitle,
  Fieldset,
  LoaderSkeleton,
  ModalV2 as Modal,
  Select,
  SelectableCard,
} from '@utilities';

import { readAskWhyQuestions } from '@api/instant_survey';
import { hydrateQuestionWithPrompts } from './utilities/helpers';
import { MAX_ASK_WHY_QUESTIONS, TOKEN_REGEX } from './utilities/constants';

import styles from './_index.module.scss';

const ModalAskWhy = ({
  askWhyGroupId,
  mustachePrompts,
  onClose,
  onPromptChange = () => {},
  onSubmit,
  report,
  submitText,
}) => {
  const askWhyOptions = report.askWhyPeopleGroups?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const [askWhyQuestions, setAskWhyQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchError, setSearchError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(
    askWhyOptions.find((option) => option.value === askWhyGroupId) || askWhyOptions[0]
  );
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const [originalPrompts] = useState(() => {
    if (typeof structuredClone === 'function') {
      return structuredClone(mustachePrompts);
    }
    return JSON.parse(JSON.stringify(mustachePrompts));
  });

  const isAnyPromptEmpty = Object.values(mustachePrompts).some(
    (value) => typeof value === 'string' && value.trim() === ''
  );

  const askWhySignal = useRef(null);
  const canAccessAskWhy = useSelector((state) => state.user.data?.permissions?.canAccessAskWhy);
  const doesAlreadyExist = report.askWhyInstantSurveys?.some(
    (survey) => survey.peopleGroupTypeId === selectedGroup?.value
  );
  const userSurveyCreditsRemaining = useSelector(
    (state) => state.user?.data?.surveyInfo?.userCredits
  );

  const selectedQuestionIds = useMemo(
    () => new Set(selectedQuestions.map((question) => question.id)),
    [selectedQuestions]
  );

  useEffect(() => {
    askWhySignal?.current?.abort();
    askWhySignal.current = new AbortController();

    const loadQuestions = async () => {
      setAskWhyQuestions([]);
      setIsLoading(true);
      setSearchError(null);
      setSelectedQuestions([]);

      try {
        const questions = await readAskWhyQuestions({
          documentId: report.documentId,
          peopleGroupTypeId: selectedGroup?.value,
          signal: askWhySignal.current.signal,
        });

        setAskWhyQuestions(questions);
      } catch (error) {
        if (!askWhySignal.current?.signal.aborted) {
          setSearchError(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
    return () => askWhySignal?.current?.abort();
  }, [report.documentId, selectedGroup]);

  const toggleSelectedQuestion = useCallback(
    (question) => {
      const exists = selectedQuestionIds.has(question.id);

      if (exists) {
        setSelectedQuestions((prevSelectedQuestions) =>
          prevSelectedQuestions.filter((selectedQuestion) => selectedQuestion.id !== question.id)
        );
      } else if (selectedQuestions.length < MAX_ASK_WHY_QUESTIONS) {
        setSelectedQuestions((prevSelectedQuestions) => [...prevSelectedQuestions, question]);
      }
    },
    [selectedQuestionIds, selectedQuestions.length]
  );

  const renderStringWithTokens = useCallback(
    (template, questionId, keyPrefix) => {
      if (!template) {
        return '';
      }

      let match;
      let lastIndex = 0;
      const nodes = [];
      const baseKey = `${questionId}-${keyPrefix}`;

      TOKEN_REGEX.lastIndex = 0;
      while ((match = TOKEN_REGEX.exec(template)) !== null) {
        const [fullMatch, mustacheKey] = match;
        const index = match.index;

        if (index > lastIndex) {
          const text = template.slice(lastIndex, index);
          nodes.push(
            <span
              className={styles['modal-ask-why-question-text']}
              key={`${baseKey}-txt-${lastIndex}`}
            >
              {text}
            </span>
          );
        }

        if (!Object.prototype.hasOwnProperty.call(mustachePrompts, mustacheKey)) {
          lastIndex = index + fullMatch.length;
          continue;
        } else {
          nodes.push(
            <span
              className={styles['modal-ask-why-inline-prompt-wrapper']}
              key={`${baseKey}-prompt-${mustacheKey}-${index}`}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <EditableTitle
                className={classNames(styles['modal-ask-why-inline-prompt'], {
                  [styles['has-error']]: mustachePrompts[mustacheKey] === '',
                })}
                onBlur={(value) => onPromptChange(mustacheKey, value || '')}
                onChange={(value) => onPromptChange(mustacheKey, value || '')}
                value={mustachePrompts[mustacheKey]}
              />
            </span>
          );
        }

        lastIndex = index + fullMatch.length;
      }

      if (lastIndex < template.length) {
        nodes.push(
          <span
            className={styles['modal-ask-why-question-text']}
            key={`${baseKey}-end-${lastIndex}`}
          >
            {template.slice(lastIndex)}
          </span>
        );
      }

      return <>{nodes}</>;
    },
    [mustachePrompts, onPromptChange]
  );

  const handleModalClose = () => {
    Object.keys(originalPrompts).forEach((key) => {
      onPromptChange(key, originalPrompts[key]);
    });
    onClose();
  };

  return (
    <Modal
      buttons={[
        <Button
          data-log={`AskWhy_Cancel_${selectedGroup?.value}`}
          id="modal-ask-why-cancel"
          key="cancel-btn"
          onClick={handleModalClose}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          data-log={`AskWhy_Continue_${selectedGroup?.value}`}
          id="modal-ask-why-continue"
          isDisabled={
            doesAlreadyExist || !selectedGroup || selectedQuestions.length === 0 || isAnyPromptEmpty
          }
          key="submit-btn"
          onClick={() =>
            onSubmit({
              selectedGroup,
              selectedQuestions: selectedQuestions.map((question) =>
                hydrateQuestionWithPrompts(question, mustachePrompts)
              ),
            })
          }
          text={submitText || 'Continue'}
        />,
      ]}
      id="modal-ask-why"
      isActive
      isScrollable
      onClose={handleModalClose}
      size="large"
      title="Find Out Why"
    >
      {canAccessAskWhy?.canPayWithCredits && userSurveyCreditsRemaining === 0 && (
        <Announcement
          text="You currently have zero credits available, please reach out to your account team if you'd like to use credits to launch."
          variant="warn"
        />
      )}
      <Select
        className={styles['modal-ask-why-select']}
        isDisabled={askWhyOptions.length === 1}
        label="Panelists (100)"
        onChange={setSelectedGroup}
        options={askWhyOptions}
        value={selectedGroup}
      />
      {searchError && (
        <Announcement
          text="No questions are associated with this group. Please select another."
          variant="error"
        />
      )}
      {isLoading && !searchError && (
        <LoaderSkeleton height={310}>
          <rect height="70" rx="4" ry="4" width="937" x="0" y="0" />
          <rect height="70" rx="4" ry="4" width="937" x="0" y="80" />
          <rect height="70" rx="4" ry="4" width="937" x="0" y="160" />
          <rect height="70" rx="4" ry="4" width="937" x="0" y="240" />
        </LoaderSkeleton>
      )}
      {!isLoading && !searchError && (
        <>
          {doesAlreadyExist && (
            <Announcement
              header="You've already sent questions for this group"
              text="You can create an Instant Survey for an in-depth survey"
              variant="warn"
            />
          )}
          <Fieldset legend={`Questions (${selectedQuestions.length}/${MAX_ASK_WHY_QUESTIONS})`}>
            <ul className={styles['modal-ask-why-questions']}>
              {askWhyQuestions.map((question) => {
                const isSelected = selectedQuestionIds.has(question.id);
                const isDisabled =
                  !isSelected && selectedQuestions.length === MAX_ASK_WHY_QUESTIONS;

                return (
                  <li data-testid="ask-why-question" key={question.id}>
                    <SelectableCard
                      data-log={`AskWhy_Questions_${selectedGroup?.value}`}
                      details={
                        question.caption ? (
                          <div className={styles['modal-ask-why-question-caption']}>
                            {renderStringWithTokens(question.caption, question.id, 'caption')}
                          </div>
                        ) : null
                      }
                      isActive={isSelected}
                      isDisabled={isDisabled}
                      isMultiSelect
                      onClick={() => toggleSelectedQuestion(question)}
                      text={
                        <div className={styles['modal-ask-why-question-content']}>
                          <div>
                            {renderStringWithTokens(question.text || '', question.id, 'text')}
                          </div>
                        </div>
                      }
                    />
                  </li>
                );
              })}
            </ul>
            {askWhyQuestions.length === 0 && <div>No results found.</div>}
          </Fieldset>
        </>
      )}
    </Modal>
  );
};

ModalAskWhy.propTypes = {
  askWhyGroupId: PropTypes.string,
  mustachePrompts: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onPromptChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  report: PropTypes.object.isRequired,
  submitText: PropTypes.string,
};

export default ModalAskWhy;
